/** In-app file viewer (design standard 2026-09-04): PDF / images / text / CSV render natively, Word (.docx) via mammoth,
 *  Excel (.xlsx/.xls) via SheetJS — all in-browser, nothing leaves the server. Anything else offers Download.
 *  Files are fetched through the signed links the API returns (url / download_url), so no auth header is needed. */
import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon, OpenInNew as OpenIcon } from '@mui/icons-material';

export type ViewableFile = { name: string; url: string; download_url?: string; content_type?: string };
const NAVY = '#0D2233';
const ext = (n: string) => (n.split('.').pop() || '').toLowerCase();
export const kindOf = (f: ViewableFile): 'pdf' | 'image' | 'text' | 'csv' | 'docx' | 'xlsx' | 'other' => {
  const ct = (f.content_type || '').toLowerCase(), x = ext(f.name);
  if (ct === 'application/pdf' || x === 'pdf') return 'pdf';
  if (ct.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(x)) return 'image';
  if (ct === 'text/csv' || x === 'csv') return 'csv';
  if (ct.startsWith('text/') || ['txt', 'log', 'md', 'json'].includes(x)) return 'text';
  if (x === 'docx' || ct.includes('wordprocessingml')) return 'docx';
  if (['xlsx', 'xls'].includes(x) || ct.includes('spreadsheetml') || ct === 'application/vnd.ms-excel') return 'xlsx';
  return 'other';
};
export const canView = (f: ViewableFile) => kindOf(f) !== 'other';

function DocxView({ url }: { url: string }) {
  const [html, setHtml] = useState<string | null>(null); const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const mammoth = await import('mammoth');
        const buf = await (await fetch(url)).arrayBuffer();
        const r = await mammoth.convertToHtml({ arrayBuffer: buf });
        if (live) setHtml(r.value);
      } catch (e: any) { if (live) setErr(e?.message || 'Could not render this document'); }
    })();
    return () => { live = false; };
  }, [url]);
  if (err) return <Typography color="error" sx={{ p: 2 }}>{err}</Typography>;
  if (html === null) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>;
  return <Box className="cc-docx" sx={{ p: 3, bgcolor: '#fff', maxWidth: 900, mx: 'auto', fontFamily: 'var(--font-body, "Source Sans 3", system-ui, sans-serif)', '& img': { maxWidth: '100%' }, '& table': { borderCollapse: 'collapse' }, '& td, & th': { border: '1px solid #D5DEE6', p: 0.5 } }} dangerouslySetInnerHTML={{ __html: html }} />;
}

function XlsxView({ url }: { url: string }) {
  const [sheets, setSheets] = useState<{ name: string; html: string }[] | null>(null); const [i, setI] = useState(0); const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const XLSX = await import('xlsx');
        const buf = await (await fetch(url)).arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        if (live) setSheets(wb.SheetNames.map((n: string) => ({ name: n, html: XLSX.utils.sheet_to_html(wb.Sheets[n], { header: '', footer: '' }) })));
      } catch (e: any) { if (live) setErr(e?.message || 'Could not render this spreadsheet'); }
    })();
    return () => { live = false; };
  }, [url]);
  if (err) return <Typography color="error" sx={{ p: 2 }}>{err}</Typography>;
  if (!sheets) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>;
  return (
    <Box sx={{ p: 2 }}>
      {sheets.length > 1 && <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>{sheets.map((s, k) => <Button key={k} size="small" variant={k === i ? 'contained' : 'outlined'} onClick={() => setI(k)} sx={{ textTransform: 'none', bgcolor: k === i ? NAVY : undefined }}>{s.name}</Button>)}</Stack>}
      <Box sx={{ overflow: 'auto', '& table': { borderCollapse: 'collapse', fontSize: 13, fontVariantNumeric: 'tabular-nums' }, '& td': { border: '1px solid #D5DEE6', px: 1, py: 0.25, whiteSpace: 'nowrap' }, '& tr:first-of-type td': { fontWeight: 600, bgcolor: '#F6F8FA' } }} dangerouslySetInnerHTML={{ __html: sheets[i].html }} />
    </Box>
  );
}

function TextView({ url, csv }: { url: string; csv: boolean }) {
  const [txt, setTxt] = useState<string | null>(null);
  useEffect(() => { fetch(url).then(r => r.text()).then(setTxt).catch(() => setTxt('Could not load file')); }, [url]);
  if (txt === null) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>;
  if (csv) {
    const rows = txt.split(/\r?\n/).filter(Boolean).map(l => l.match(/("([^"]|"")*"|[^,]*)(,|$)/g)?.map(c => c.replace(/,$/, '').replace(/^"|"$/g, '').replace(/""/g, '"')) || []);
    return <Box sx={{ p: 2, overflow: 'auto' }}><table style={{ borderCollapse: 'collapse', fontSize: 13 }}><tbody>{rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} style={{ border: '1px solid #D5DEE6', padding: '2px 8px', fontWeight: i === 0 ? 600 : 400, background: i === 0 ? '#F6F8FA' : undefined }}>{c}</td>)}</tr>)}</tbody></table></Box>;
  }
  return <Box component="pre" sx={{ p: 2, m: 0, whiteSpace: 'pre-wrap', fontFamily: 'var(--font-mono, "Source Code Pro", ui-monospace, monospace)', fontSize: 13 }}>{txt}</Box>;
}

export default function FileViewer({ file, onClose }: { file: ViewableFile | null; onClose: () => void }) {
  if (!file) return null;
  const k = kindOf(file); const dl = file.download_url || file.url;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { height: '92vh', display: 'flex', flexDirection: 'column' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, color: NAVY, fontWeight: 700, fontSize: 16 }}>
        <Typography component="span" noWrap sx={{ flex: 1, fontWeight: 700 }} title={file.name}>{file.name}</Typography>
        <Button component="a" href={file.url} target="_blank" rel="noreferrer" size="small" startIcon={<OpenIcon />} sx={{ textTransform: 'none' }}>Open in tab</Button>
        <Button component="a" href={dl} size="small" variant="outlined" startIcon={<DownloadIcon />} sx={{ textTransform: 'none' }}>Download</Button>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, flex: 1, bgcolor: '#F3F6F9', overflow: 'auto' }}>
        {k === 'pdf' && <Box component="iframe" src={file.url} title={file.name} sx={{ width: '100%', height: '100%', border: 0, display: 'block' }} />}
        {k === 'image' && <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', p: 2 }}><Box component="img" src={file.url} alt={file.name} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /></Box>}
        {(k === 'text' || k === 'csv') && <TextView url={file.url} csv={k === 'csv'} />}
        {k === 'docx' && <DocxView url={file.url} />}
        {k === 'xlsx' && <XlsxView url={file.url} />}
        {k === 'other' && (
          <Stack sx={{ p: 4, alignItems: 'center', gap: 2 }}>
            <Typography>This file type can't be shown in the browser. Download it to open on your computer.</Typography>
            <Button component="a" href={dl} variant="contained" startIcon={<DownloadIcon />} sx={{ bgcolor: NAVY, textTransform: 'none' }}>Download {file.name}</Button>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
